import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { createReturnRouted, getInvoiceForReturnRouted } from '@/offline';
import { X, Search, RotateCcw } from 'lucide-react';
import BankAccountSelect from '@/components/common/BankAccountSelect';
import ManagerOverrideModal from '@/components/pos/ManagerOverrideModal';
import { useAuthStore } from '@/store/authStore';
import type { ReturnableInvoice } from '@/types';
import { formatMoney } from '@/utils/currency';
import { translatePaymentMethod } from '@/utils/translateStatus';
import { canBypassStaffLimits, refundExceedsLimits } from '@/utils/staffPermissions';

interface CustomerReturnModalProps {
  openShiftName?: string;
  initialInvoice?: string;
  onClose: () => void;
  onSuccess: (result: { name: string; refund_amount: number }) => void;
}

export default function CustomerReturnModal({
  openShiftName,
  initialInvoice,
  onClose,
  onSuccess,
}: CustomerReturnModalProps) {
  const { t } = useTranslation();
  const { permissions, role } = useAuthStore();
  const [invoiceNo, setInvoiceNo] = useState(initialInvoice || '');
  const [invoice, setInvoice] = useState<ReturnableInvoice | null>(null);
  const [returnQty, setReturnQty] = useState<Record<string, string>>({});
  const [returnMethod, setReturnMethod] = useState<'Cash' | 'Bank' | 'Exchange'>('Cash');
  const [selectedBank, setSelectedBank] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [showManagerOverride, setShowManagerOverride] = useState(false);
  const [pendingManagerApprover, setPendingManagerApprover] = useState<string | null>(null);

  const { data: banksData } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => { const res = await posApi.getBankAccounts(); return res.data.message || []; },
  });
  const banks = banksData || [];

  const { data: cashierReturnCount = 0 } = useQuery({
    queryKey: ['myShiftReturnCount', openShiftName],
    queryFn: async () => {
      const res = await posApi.getMyShiftReturnCount(openShiftName!);
      return Number(res.data.message) || 0;
    },
    enabled: !!openShiftName,
  });
  
  const defaultBank =
    banks.find((b: { name: string; is_default: boolean }) => b.name === selectedBank)
    || banks.find((b: { is_default: boolean }) => b.is_default)
    || banks[0];

  const lookupMutation = useMutation({
    mutationFn: async (invoice: string) => {
      const data = await getInvoiceForReturnRouted(invoice);
      return data as ReturnableInvoice;
    },
    onSuccess: (data) => {
      setInvoice(data);
      setLookupError('');
      const initial: Record<string, string> = {};
      data.lines.forEach((line) => {
        initial[line.invoice_item] = line.returnable_qty > 0 ? line.returnable_qty.toString() : '';
      });
      setReturnQty(initial);
    },
    onError: (err: unknown) => {
      setInvoice(null);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setLookupError(typeof msg === 'string' ? msg : t('returnModal.invoiceNotFound'));
    },
  });

  useEffect(() => {
    if (initialInvoice) {
      lookupMutation.mutate(initialInvoice);
    }
  }, [initialInvoice]);

  const returnMutation = useMutation({
    mutationFn: async (managerApprover?: string) => {
      if (!invoice) throw new Error('Look up an invoice first');
      const items = invoice.lines
        .map((line) => {
          const qty = parseFloat(returnQty[line.invoice_item] || '0');
          if (qty > line.returnable_qty) {
            throw new Error(`Cannot return more than ${line.returnable_qty} for ${line.item_name}`);
          }
          return {
            invoice_item: line.invoice_item,
            quantity: qty,
          };
        })
        .filter((row) => row.quantity > 0);

      if (items.length === 0) {
        throw new Error('Please enter a quantity for at least one item.');
      }

      const result = await createReturnRouted({
        pos_invoice: invoice.name,
        pos_shift: openShiftName,
        items,
        return_method: returnMethod,
        return_bank: returnMethod === 'Bank' ? (selectedBank || defaultBank?.name || '') : undefined,
        ...(managerApprover ? { manager_approver: managerApprover } : {}),
      });
      if (typeof result === 'string') {
        return { name: result, refund_amount: 0 };
      }
      return result as { name: string; refund_amount: number };
    },
    onSuccess: (result) => {
      setSubmitError('');
      onSuccess(result);
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof Error && !('response' in err)) {
        setSubmitError(err.message);
        return;
      }
      let msg = (err as any)?.response?.data?.message;
      const serverMessages = (err as any)?.response?.data?._server_messages;
      if (serverMessages) {
        try {
          const parsed = JSON.parse(serverMessages);
          if (parsed && parsed.length > 0) {
            const inner = JSON.parse(parsed[0]);
            if (inner.message) msg = inner.message;
          }
        } catch (e) {
          // ignore parsing error
        }
      }
      setSubmitError(typeof msg === 'string' ? msg : 'Return failed');
    },
  });

  const grossRefund = invoice
    ? invoice.lines.reduce((sum, line) => {
        const qty = parseFloat(returnQty[line.invoice_item] || '0') || 0;
        return sum + qty * line.rate;
      }, 0)
    : 0;

  let refundDiscount = 0;
  if (invoice && invoice.discount_amount > 0 && invoice.item_total > 0) {
    refundDiscount = (grossRefund / invoice.item_total) * invoice.discount_amount;
  }
  
  let refundTotal = grossRefund - refundDiscount;
  if (invoice && refundTotal > invoice.refundable_cash) {
    refundTotal = invoice.refundable_cash;
  }

  const handleLookup = (e?: React.FormEvent) => {
    e?.preventDefault();
    const code = invoiceNo.trim();
    if (!code) return;
    lookupMutation.mutate(code);
  };

  const handleConfirmReturn = () => {
    setSubmitError('');
    const needsOverride =
      !canBypassStaffLimits(role) &&
      refundExceedsLimits(refundTotal, permissions, cashierReturnCount) &&
      !pendingManagerApprover;

    if (needsOverride) {
      setShowManagerOverride(true);
      return;
    }
    returnMutation.mutate(pendingManagerApprover || undefined);
  };

  const refundLimitHint = (() => {
    const parts: string[] = [];
    if (permissions?.max_refund_amount && permissions.max_refund_amount > 0) {
      parts.push(`Max per return: ${formatMoney(permissions.max_refund_amount)}`);
    }
    if (permissions?.max_refund_count && permissions.max_refund_count > 0) {
      parts.push(`Returns this shift: ${cashierReturnCount}/${permissions.max_refund_count}`);
    }
    return parts.join(' · ');
  })();

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-primary-600" />
            {t('returnModal.title')}
          </h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-auto flex-1 min-h-0">
          <form onSubmit={handleLookup} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                className="input-field pl-9"
                placeholder={t('returnModal.invoicePlaceholder')}
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={lookupMutation.isPending || !invoiceNo.trim()}
              className="btn-secondary shrink-0"
            >
              {lookupMutation.isPending ? '...' : t('returnModal.lookup')}
            </button>
          </form>
          {lookupError && <p className="text-sm text-red-600">{lookupError}</p>}

          {invoice && (
            <>
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 text-sm space-y-1">
                <div className="font-medium text-gray-900">{invoice.name}</div>
                <div className="text-gray-600">{invoice.customer_name || t('returnModal.walkIn')}</div>
                <div className="text-gray-500">
                  {t('returnModal.paid')} {formatMoney(invoice.paid_amount)}
                  {' · '}{t('returnModal.alreadyRefunded')} {formatMoney(invoice.total_refunded)}
                  {' · '}{t('returnModal.maxCashRefund')} {formatMoney(invoice.refundable_cash)}
                </div>
                {refundLimitHint && (
                  <div className="text-xs text-amber-700">{refundLimitHint}</div>
                )}
              </div>

              <div className="border border-gray-100 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">{t('returnModal.item')}</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600 w-20">{t('returnModal.sold')}</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600 w-24">{t('returnModal.returned')}</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600 w-28">{t('returnModal.returnNow')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoice.lines.map((line) => (
                      <tr key={line.invoice_item}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{line.item_name}</div>
                          {line.variant_name && (
                            <div className="text-xs text-gray-500">{line.variant_name}</div>
                          )}
                          <div className="text-xs text-gray-400">{formatMoney(line.rate)} {t('returnModal.each')}</div>
                        </td>
                        <td className="px-3 py-2 text-right">{line.sold_qty}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{line.returned_qty}</td>
                        <td className="px-3 py-2 text-right">
                          {line.returnable_qty > 0 ? (
                            <input
                              type="number"
                              min="0"
                              max={line.returnable_qty}
                              step="1"
                              className="input-field text-right w-20 ml-auto py-1"
                              value={returnQty[line.invoice_item] || ''}
                              onChange={(e) =>
                                setReturnQty({ ...returnQty, [line.invoice_item]: e.target.value })
                              }
                            />
                          ) : (
                            <span className="text-xs text-gray-400">{t('returnModal.done')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-3 mt-4 text-sm">
                <div className="flex flex-wrap items-center gap-6 border-t border-gray-100 pt-3">
                  <div className="flex items-center gap-3">
                    <label className="font-medium text-gray-700 w-32">{t('returnModal.returnMethod')}</label>
                    <select
                      className="input-field w-[160px] py-1.5"
                      value={returnMethod}
                      onChange={(e) => setReturnMethod(e.target.value as 'Cash' | 'Bank' | 'Exchange')}
                    >
                      <option value="Cash">{translatePaymentMethod('Cash', t)}</option>
                      <option value="Bank">{translatePaymentMethod('Bank', t)}</option>
                    </select>
                  </div>
                  {returnMethod === 'Bank' && (
                    <div className="flex items-center gap-3">
                      <label className="font-medium text-gray-700">{t('returnModal.selectBank')}</label>
                      <div className="w-[180px]">
                        <BankAccountSelect
                          value={selectedBank || defaultBank?.name || ''}
                          onChange={setSelectedBank}
                          banks={banks}
                          size="sm"
                          required
                        />
                        {banks.length === 0 && (
                          <p className="text-[10px] text-amber-600 mt-0.5">{t('returnModal.noBanks')}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 pt-2 text-sm">
                  <div className="flex items-center justify-between text-gray-600">
                    <span>{t('returnModal.itemsTotal')}</span>
                    <span>{formatMoney(grossRefund)}</span>
                  </div>
                  {refundDiscount > 0 && (
                    <div className="flex items-center justify-between text-amber-600">
                      <span>{t('returnModal.proportionalDiscount')}</span>
                      <span>- {formatMoney(refundDiscount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between font-medium pt-2 border-t border-gray-100 mt-1">
                    <span className="text-gray-900">{t('returnModal.netRefund')}</span>
                    <span className="text-lg font-bold text-primary-600">{formatMoney(refundTotal)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {submitError && <p className="text-sm text-red-600">{submitError}</p>}
        </div>

        <div className="p-5 border-t border-gray-100 bg-gray-50 flex gap-3 shrink-0">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">{t('common.cancel')}</button>
          <button
            type="button"
            disabled={!invoice || returnMutation.isPending || refundTotal <= 0}
            onClick={handleConfirmReturn}
            className="btn-primary flex-1"
          >
            {returnMutation.isPending ? t('returnModal.processing') : t('returnModal.confirmReturn')}
          </button>
        </div>
      </div>
    </div>

    <ManagerOverrideModal
      open={showManagerOverride}
      onClose={() => setShowManagerOverride(false)}
      onApproved={(approverId) => {
        setPendingManagerApprover(approverId);
        setShowManagerOverride(false);
        returnMutation.mutate(approverId);
      }}
      message="Refund exceeds your limit. Manager approval required."
    />
    </>
  );
}
